/**
 * Type definitions for the Appearance System
 */

export type Theme = 'light' | 'dark' | 'system';
export type FontSize = 'small' | 'medium' | 'large';
export type FontFamily = 'system' | 'inter' | 'roboto' | 'poppins' | 'mono';
export type AccentColor = 'shiftleft' | 'slate' | 'blue' | 'purple' | 'green' | 'rose' | 'amber' | 'cyan';

/** Settings for a single UI component */
export interface ComponentAppearance {
    theme?: Theme;
    fontSize?: FontSize;
    fontFamily?: FontFamily;
    accentColor?: AccentColor;
    // Extensible: add more properties as needed
    customCSS?: string;
}

/** Full appearance configuration with global + per-component settings */
export interface AppearanceConfig {
    global: ComponentAppearance;
    components: Record<string, ComponentAppearance>;
}

/** UI Component definition for the settings panel */
export interface UIComponentConfig {
    id: string;
    label: string;
    description: string;
    icon?: React.ComponentType<{ className?: string }>;
}

/** Default UI components that most apps have */
export const DEFAULT_UI_COMPONENTS: UIComponentConfig[] = [
    { id: 'global', label: 'Global', description: 'Apply to all components' },
    { id: 'topNav', label: 'Top Navigation', description: 'Header and main navigation bar' },
    { id: 'leftPanel', label: 'Left Panel', description: 'Sidebar navigation menu' },
    { id: 'mainPanel', label: 'Main Panel', description: 'Primary content area' },
    { id: 'cards', label: 'Cards & Widgets', description: 'Dashboard cards and KPIs' },
    { id: 'rightPanel', label: 'Right Panel', description: 'Detail and agent panels' },
];

/** Color presets with their hex values */
export const ACCENT_COLORS: { value: AccentColor; label: string; color: string }[] = [
    { value: 'shiftleft', label: 'Navy', color: '#2E3A59' },
    { value: 'slate', label: 'Slate', color: '#475569' },
    { value: 'blue', label: 'Blue', color: '#2563eb' },
    { value: 'purple', label: 'Purple', color: '#7c3aed' },
    { value: 'green', label: 'Green', color: '#16a34a' },
    { value: 'rose', label: 'Rose', color: '#e11d48' },
    { value: 'amber', label: 'Amber', color: '#d97706' },
    { value: 'cyan', label: 'Cyan', color: '#0891b2' },
];

/** Font family options with CSS values */
export const FONT_FAMILIES: { value: FontFamily; label: string; css: string }[] = [
    { value: 'system', label: 'System Default', css: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
    { value: 'inter', label: 'Inter', css: '"Inter", sans-serif' },
    { value: 'roboto', label: 'Roboto', css: '"Roboto", sans-serif' },
    { value: 'poppins', label: 'Poppins', css: '"Poppins", sans-serif' },
    { value: 'mono', label: 'Monospace', css: '"JetBrains Mono", "Fira Code", monospace' },
];

/** Font size options with pixel values */
export const FONT_SIZES: { value: FontSize; label: string; css: string }[] = [
    { value: 'small', label: 'Small', css: '14px' },
    { value: 'medium', label: 'Medium', css: '16px' },
    { value: 'large', label: 'Large', css: '18px' },
];

/** Default appearance configuration */
export const DEFAULT_APPEARANCE: AppearanceConfig = {
    global: {
        theme: 'light',
        fontSize: 'medium',
        fontFamily: 'system',
        accentColor: 'shiftleft',
    },
    components: {},
};
